import * as angular from 'angular';
import * as _ from 'lodash';
import { Workspace } from '../../models';

import { IAuthentication, IAuthenticationService, ILoginData, ILoginResult } from '../../services';
import { Dispatcher, Store } from '../Store';

import * as ACTIONS from './authentication.constants';
import { APPLICATION_DISCONNECTED } from '../application/application.constants';


export interface IAuthenticationActionsService {
    connect(userName: string, workspace: Workspace, applicationGuid: string, showToast: boolean): Dispatcher<IAuthentication>;
    connectByApplicationName(applicationName: string, applicationGuid: string): Dispatcher<IAuthentication>;
    connectByWorkspace(userName: string, workspace: Workspace): Dispatcher<IAuthentication>;
    disconnect(): Dispatcher<void>;
    fillAuthData(): Dispatcher<Readonly<IAuthentication>>;
    get(): Dispatcher<Readonly<IAuthentication>>;
    login(loginData: ILoginData, logoutOnFail?: boolean): Dispatcher<ILoginResult>;
    logout(): Dispatcher<IAuthentication>;
    updateUserData(values: { datePickerFormat?: string }): Dispatcher<void>;
}

export class AuthenticationActionsService implements IAuthenticationActionsService {

    static serviceId = 'authenticationActionsService';
    static $inject = ['$q', 'authenticationService'];


    constructor(
        private _$q: angular.IQService,
        private _authenticationService: IAuthenticationService
    ) {
    }

    connect(userName: string, workspace: Workspace, applicationGuid: string, showToast: boolean) {
        return (dispatch: any, getState: () => Store) => {
            dispatch({ type: ACTIONS.AUTHENTICATION_LOAD });
            return this._authenticationService
                .connect(userName, workspace, applicationGuid, showToast)
                .finally(() => {
                    dispatch({ type: ACTIONS.AUTHENTICATION_LOAD_SUCCESS, payload: this._authenticationService.authentication });
                    return this._$q.when(this._authenticationService.authentication);
                });
        };
    }

    connectByApplicationName(applicationName: string, applicationGuid: string) {
        return (dispatch: any, getState: () => Store) => {
            dispatch({ type: ACTIONS.AUTHENTICATION_LOAD });
            return this._authenticationService
                .connectByApplicationName(applicationName, applicationGuid)
                .finally(() => {
                    dispatch({ type: ACTIONS.AUTHENTICATION_LOAD_SUCCESS, payload: this._authenticationService.authentication });
                    return this._$q.when(this._authenticationService.authentication);
                });
        };
    }

    connectByWorkspace(userName: string, workspace: Workspace) {
        return (dispatch: any, getState: () => Store) => {
            dispatch({ type: ACTIONS.AUTHENTICATION_LOAD });
            return this._authenticationService
                .connectByWorkspace(userName, workspace)
                .finally(() => {
                    dispatch({ type: ACTIONS.AUTHENTICATION_LOAD_SUCCESS, payload: this._authenticationService.authentication });
                    return this._$q.when(this._authenticationService.authentication);
                });
        };
    }

    disconnect() {
        return (dispatch: any, getState: () => Store) => {
            dispatch({ type: ACTIONS.AUTHENTICATION_LOAD });
            dispatch({ type: APPLICATION_DISCONNECTED });
            this._authenticationService.disconnect();
            dispatch({ type: ACTIONS.AUTHENTICATION_LOAD_SUCCESS, payload: this._authenticationService.authentication });
            return this._$q.when();
        };
    }

    login(loginData: ILoginData, logoutOnFail?: boolean): Dispatcher<ILoginResult> {
        return (dispatch: any, getState: () => Store) => {
            dispatch({ type: ACTIONS.AUTHENTICATION_LOAD });
            return this._authenticationService
                .login(loginData, logoutOnFail)
                .then(data => {
                    dispatch({ type: ACTIONS.AUTHENTICATION_LOAD_SUCCESS, payload: this._authenticationService.authentication });
                    return data;
                })
                .catch(reason => {
                    dispatch({ type: ACTIONS.AUTHENTICATION_LOAD_ERROR, payload: this._authenticationService.authentication });
                    throw reason;
                });
        };
    }

    logout() {
        return (dispatch: any, getState: () => Store) => {
            dispatch({ type: ACTIONS.AUTHENTICATION_LOAD });
            this._authenticationService.logout();
            const auth = this._authenticationService.authentication;
            dispatch({ type: ACTIONS.AUTHENTICATION_LOAD_SUCCESS, payload: auth });
            return this._$q.when(auth);
        };
    }

    fillAuthData() {
        return (dispatch: any, getState: () => Store) => {
            dispatch({ type: ACTIONS.AUTHENTICATION_LOAD });
            return this._authenticationService.getAuthData()
                .then(data => {
                    const auth = data;
                    dispatch({ type: ACTIONS.AUTHENTICATION_LOAD_SUCCESS, payload: auth });
                    return auth;
                });
        };
    }

    get() {
        return (dispatch: any, getState: () => Store) => {
            const { authentication } = getState();
            if (!authentication.isLoading && !authentication.isLoaded) {
                // { type: APPROVALSETTINGS.APPROVAL_SETTINGS_LOAD_SUCCESS, payload: data }
                dispatch({ type: ACTIONS.AUTHENTICATION_LOAD });
                return this._$q
                    .when(this._authenticationService.authentication)
                    .then(auth => {
                        dispatch({ type: ACTIONS.AUTHENTICATION_LOAD_SUCCESS, payload: auth });
                        return auth;
                    })
                    .catch(reason => {
                        dispatch({ type: ACTIONS.AUTHENTICATION_LOAD_ERROR, payload: reason });
                        return reason;
                    });
            } else {
                return this._$q.when(authentication);
            }
        };
    }

    updateUserData(values: { datePickerFormat?: string }): Dispatcher<void> {
        return (dispatch: any, getState: () => Store) => {
            this._authenticationService.updateUserData(values);
            return this._$q.resolve();
        };
    }
}